import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * useCreatePreInvoice - Hook para crear prefacturas desde shipments seleccionados
 * 
 * Ejecuta POST /invoices/pre-invoices con array de shipment_ids para crear una nueva prefactura.
 * Maneja estados de loading, éxito y error con toasts apropiados.
 * 
 * @returns {Object} Función createPreInvoice y estado isCreating
 */
export const useCreatePreInvoice = () => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  const createPreInvoice = async (
    shipmentIds: number[],
    onSuccess?: () => void
  ) => {
    setIsCreating(true);

    try {
      console.log('🔍 useCreatePreInvoice - Creando prefactura con shipments:', { shipmentIds, count: shipmentIds.length });

      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.post<any>(
        '/invoices/pre-invoices',
        { shipment_ids: shipmentIds }
      );

      console.log('🔍 useCreatePreInvoice - Respuesta:', response);

      // 🎯 Si la API devuelve 200 OK sin la propiedad 'success', considerarlo éxito
      const isSuccess = response?.success !== false && (response?.success === true || typeof response?.success === 'undefined');
      
      if (isSuccess) {
        // 🎯 Mostrar toast de éxito con mensaje de la API
        toast({
          title: t('toast.create_preinvoice_success.title', 'Prefactura creada'),
          description: response?.message || t('toast.create_preinvoice_success.description', {
            count: shipmentIds.length,
            defaultValue: `Se ha creado la prefactura con ${shipmentIds.length} envíos exitosamente`,
          }),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch, limpiar selección, etc.)
        onSuccess?.();
      } else {
        // 🎯 Mostrar error devuelto por la API
        toast({
          title: t('toast.create_preinvoice_error.title', 'Error al crear prefactura'),
          description: response?.message || t('toast.create_preinvoice_error.unexpected'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 useCreatePreInvoice - Error:', error);

      // 🎯 Mostrar error inesperado
      toast({
        title: t('toast.create_preinvoice_error.title', 'Error al crear prefactura'),
        description: error?.message || t('toast.create_preinvoice_error.unexpected', 'Error inesperado al crear la prefactura. Inténtalo de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createPreInvoice,
    isCreating,
  };
};

